import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Podcast } from 'src/types/coaching/podcast';

import _ from 'lodash';
import cx from 'classnames';

import WaveSurfer from 'wavesurfer.js';

import styles from './player.module.scss';

import { animated, useSpring, useTransition } from '@react-spring/web';
import { SoundVisualizer } from 'src/pages/coaching/components/simulation/view/simulator/visualiser';

interface PodcastMiniPlayerProps {
  podcast: Podcast;
  onPlay?: () => void;
}

export const PodcastMiniPlayer: React.FC<PodcastMiniPlayerProps> = ({ podcast, onPlay }) => {
  const containerRef = React.useRef(null);
  const playerRef = React.useRef<WaveSurfer | null>(null);

  const {url} = useMemo(() => {
    const audio = _.get(podcast, 'audio');
    const url = _.get(audio, 'url') || "";
    const duration = _.get(audio, 'duration', 0);

    return {
      url: url,
      duration: duration
    }
  }, [podcast]);

  const [playerState, setPlayerState] = useState({
    muted: false,
    playbackRate: 1,
    volume: 100,
    duration: 0,
    currentTime: 0,
    waveformLoaded: false,
    audioPlaying: false
  });

  const loadingTransition = useTransition(!playerState.waveformLoaded, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const [playerStyles] = useSpring({
    transform: playerState.waveformLoaded ? "translateY(0%)" : "translateY(100%)",
    opacity: playerState.waveformLoaded ? 1 : 0,
  }, [playerState.waveformLoaded]);

  useEffect(() => {
    if (url && WaveSurfer) {
      loadPlayer()
    }

    return () => {
      if (playerRef.current) {
        try {
          playerRef.current.destroy();
        }catch (e) {
          console.error("Error destroying WaveSurfer instance: ", e);
        }

        playerRef.current = null;
      }
    };
  }, [url]);


  useEffect(() => {
    if (playerState.audioPlaying && onPlay) {
      onPlay();
    }
  }, [playerState.audioPlaying]);

  const loadPlayer = () => {
    playerRef.current = WaveSurfer.create({
      container: containerRef.current!,
      height: 56,
      waveColor: '#DBDBDB',
      progressColor: "#666666",
      barWidth: 2,
      barGap: 3,
      barRadius: 2,
      cursorWidth: 0,
      normalize: true,
      hideScrollbar: true,
    });

    playerRef.current.load(url);

    //Event listeners for player
    playerRef.current.on('ready', () => {
      setPlayerState((state) => {
        return {
          ...state,
          duration: Math.round(playerRef.current?.getDuration() || 0),
          waveformLoaded: true
        }
      });

      playerRef.current?.setTime(0);
    });

    playerRef.current.on('play', () => {
      setPlayerState((state) => {
        return {
          ...state,
          audioPlaying: playerRef.current?.isPlaying() || false,
        }
      });
    });

    playerRef.current.on('pause', () => {
      setPlayerState((state) => {
        return {
          ...state,
          audioPlaying: playerRef.current?.isPlaying() || false,
        }
      });
    });

    playerRef.current.on('audioprocess', () => {
      setPlayerState((state) => {
        return {
          ...state,
          currentTime: Math.round(playerRef.current?.getCurrentTime() || 0)
        }
      });
    });

    playerRef.current.on('seeking', () => {
      setPlayerState((state) => {
        return {
          ...state,
          currentTime: playerRef.current?.getCurrentTime() || 0,
        }
      });
    });

    playerRef.current.on('finish', () => {
      setPlayerState((state) => {
        return {
          ...state,
          audioPlaying: playerRef.current?.isPlaying() || false,
          currentTime: 0
        }
      });
    });

    playerRef.current.on('error', (e) => {
      setPlayerState((state) => {
        return {
          ...state,
          error: e
        }
      })
    });
  };

  const onPlayPause = useCallback(() => {
    if (playerRef.current) {
      playerRef.current.playPause();
    }
  }, [playerRef.current])

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    const paddedMins = String(mins).padStart(2, '0');
    const paddedSecs = String(secs).padStart(2, '0');

    if (hrs === 0) {
      return `${paddedMins}:${paddedSecs}`;
    } else {
      const paddedHrs = String(hrs);
      return `${paddedHrs}:${paddedMins}:${paddedSecs}`;
    }
  }

  return (
    <div className={styles.podcastPlayerContainer}>
      {loadingTransition((style, item) => item && (
        <animated.div className={styles.loading} style={style}>
          <AudioVisualizerLoader />
        </animated.div>
      ))}

      <animated.div className={styles.podcastPlayer} style={playerStyles}>
        <div className={cx(styles.actions, styles.left)}>
          <button onClick={onPlayPause} className={cx(styles.action, styles.play)}>
            <i className={playerState.audioPlaying ? "paperflite-pause" : "paperflite-play-outline"}></i>
          </button>
        </div>
        <div className={styles.track}>
          <div className={styles.inner}>
            <div ref={containerRef} className={styles.waveform} />
            <div className={styles.totalTime}>{formatTime(playerState.currentTime || 0)} / {formatTime(playerState.duration || 0)}</div>
          </div>
        </div>
      </animated.div>
    </div>
  );
}

const AudioVisualizerLoader: React.FC = () => {
  return (
    <div className={styles.audioVisualizerLoader}>
      <SoundVisualizer
        disabled={false}
        volumeLevel={1}
        barCount={120}
        barWidth="2px"
        height={30}
        color="#FF45C2"
        durationInSeconds={0.25}
        cornerRadius={8}
        verticalAlignment="center"
      />
      <div className={styles.message}>Analyzing segment boundaries...</div>
    </div>
  );
}